import { Control, ControlsTreeContext } from "@/types/controls";
import { BuilderElementControls } from "@/types/elements";
import defensiveCopy from "@/util/defensive-copy";
import { valuePresent } from "@/lib/controls/values";

export function filterBuilderElementControls(
  controls: BuilderElementControls,
  onlyShowModified: boolean,
  searchQuery: string,
  context: ControlsTreeContext
): BuilderElementControls {
  const regexSearchQuery = new RegExp(
    searchQuery.trim().replace(" ", "|"),
    "si"
  );

  const callback = (control: Control, path: string) => {
    if (
      regexSearchQuery &&
      !`${control.label}`.match(regexSearchQuery) &&
      !`${control.slug}`.match(regexSearchQuery) &&
      !`${control.keywords}`.match(regexSearchQuery)
    ) {
      return false;
    }

    if (onlyShowModified && !valuePresent(path, control, context)) {
      return false;
    }

    /*
    we could use shouldShowControl here, but I think
    it is better to show all the controls when searching,
    even if they would be hidden by a condition. maybe the right
    UX would be to grey them out though.
    */

    return true;
  };

  return {
    contentSections: _filterControls(
      controls.contentSections,
      callback,
      "content",
      onlyShowModified
    ),
    designSections: _filterControls(
      controls.designSections,
      callback,
      "design",
      onlyShowModified
    ),
    settingsSections: _filterControls(
      controls.settingsSections,
      callback,
      "settings",
      onlyShowModified
    )
  };
}

export function filterControls(
  controls: Control[],
  onlyShowModified: boolean,
  searchQuery: string,
  context: ControlsTreeContext,
  path: string
): Control[] {
  const regexSearchQuery = new RegExp(
    searchQuery.trim().replace(" ", "|"),
    "si"
  );

  const callback = (control: Control, path: string) => {
    if (
      regexSearchQuery &&
      !`${control.label}`.match(regexSearchQuery) &&
      !`${control.slug}`.match(regexSearchQuery) &&
      !`${control.keywords}`.match(regexSearchQuery)
    ) {
      return false;
    }

    if (onlyShowModified && !valuePresent(path, control, context)) {
      return false;
    }

    /*
    we could use shouldShowControl here, but I think
    it is better to show all the controls when searching,
    even if they would be hidden by a condition. maybe the right
    UX would be to grey them out though.
    */

    return true;
  };

  return _filterControls(controls, callback, path, onlyShowModified);
}

export function _filterControls(
  controls: Control[],
  callback: (control: Control, path: string) => boolean,
  path: string,
  onlyShowModified: boolean
) {
  const controls_ = defensiveCopy(controls);
  return controls_.filter(control => {
    const outcome = callback(control, `${path}.${control.slug}`);
    if (control.options.type === "section") {
      if (outcome && !onlyShowModified) {
        return true;
      }
      control.children = _filterControls(
        control.children,
        callback,
        `${path}.${control.slug}`,
        onlyShowModified
      );
      return control.children.length > 0;
    }
    return outcome;
  });
}

export const flattenAndCount = (controls: Control[], acc: number) => {
  controls.forEach(control => {
    acc = flattenAndCount(
      control.children,
      acc + (control.options.type === "section" ? 0 : 1)
    );
  });
  return acc;
};
